import { Result } from '@invisionag/next/result';
import { getClientHeaders } from '@invisionag/next/client';
import { paths } from '../../generated-types/cortex';
import { cortexBrowserEndpoint } from '@/environment';

const Path = {
  login: '/i/login',
  otpVerification: '/i/login/otp-verification',
  recoveryCodeVerification: '/i/login/recovery-code-verification',
  '2fa': '/i/profile/2fa',
  '2faSudo': '/i/profile/2fa/sudo',
  '2faVerification': '/i/profile/2fa/verify',
  loginSSO: '/i/login/sso',
  requestPasswordReset: '/i/passwords/reset-request',
  passwordReset: '/i/passwords/reset',
  initialPassword: '/i/passwords/initial',
} as const satisfies Record<string, keyof paths>;

type Path = typeof Path;

type Login = paths[Path['login']]['post'];
type LoginAcceptedResponse =
  Login['responses']['202']['content']['application/json'];

type IncompleteLogin = LoginAcceptedResponse['data'];

export type LoginError = 'invalid-credentials';
export type LoginResult = Result<void | IncompleteLogin, LoginError>;

export async function login(
  email: string,
  password: string,
): Promise<LoginResult> {
  const response = await fetch(`${cortexBrowserEndpoint}${Path.login}`, {
    method: 'POST',
    body: JSON.stringify({ data: { email, password } }),
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });

  switch (response.status) {
    case 204:
      return Result.Ok();
    case 202: {
      const { data }: LoginAcceptedResponse = await response.json();
      return Result.Ok(data);
    }
    case 401:
      return Result.Err('invalid-credentials');
    default:
      throw new Error(`Unexpected status '${response.status}'`);
  }
}

export type OtpVerificationError = 'invalid-otp' | 'timeout';
export type OtpVerificationResult = Result<void, OtpVerificationError>;

export async function verifyOtp(otp: string): Promise<OtpVerificationResult> {
  const response = await fetch(
    `${cortexBrowserEndpoint}${Path.otpVerification}`,
    {
      method: 'POST',
      body: JSON.stringify({ data: { otp } }),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  switch (response.status) {
    case 204:
      return Result.Ok();
    case 401:
      return Result.Err('invalid-otp');
    case 403:
      return Result.Err('timeout');
    default:
      throw new Error(`Unexpected status '${response.status}'`);
  }
}

export type RecoveryCodeVerificationError = 'invalid-recovery-code' | 'timeout';
export type RecoveryCodeVerificationResult = Result<
  void,
  RecoveryCodeVerificationError
>;

export async function verifyRecoveryCode(
  recoveryCode: string,
): Promise<RecoveryCodeVerificationResult> {
  const response = await fetch(
    `${cortexBrowserEndpoint}${Path.recoveryCodeVerification}`,
    {
      method: 'POST',
      body: JSON.stringify({ data: { recoveryCode } }),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  switch (response.status) {
    case 204:
      return Result.Ok();
    case 401:
      return Result.Err('invalid-recovery-code');
    case 403:
      return Result.Err('timeout');
    default:
      throw new Error(`Unexpected status '${response.status}'`);
  }
}

export type ConfirmSudoError = 'invalid-password';
export type ConfirmSudoResult = Result<void, ConfirmSudoError>;

export async function confirm2faSudo(
  password: string,
): Promise<ConfirmSudoResult> {
  const response = await fetch(`${Path['2faSudo']}`, {
    method: 'POST',
    body: JSON.stringify({
      data: { type: 'two-factor-sudo', attributes: { password } },
    }),
    headers: getClientHeaders({ 'Content-Type': 'application/vnd.api+json' }),
  });

  switch (response.status) {
    case 204:
      return Result.Ok();
    case 412:
      return Result.Err('invalid-password');
    default:
      throw new Error(`Unexpected status '${response.status}'`);
  }
}

type TwoFactorAuthenticationResponse =
  paths[Path['2fa']]['post']['responses']['201']['content']['application/json'];

export type TwoFactorAuthentication = TwoFactorAuthenticationResponse['data'];

export async function create2fa(): Promise<TwoFactorAuthentication> {
  const response = await fetch(`${Path['2fa']}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Accept-Version': '2' },
  });

  switch (response.status) {
    case 201: {
      const { data }: TwoFactorAuthenticationResponse = await response.json();
      return data;
    }
    default:
      throw new Error(`Unexpected status '${response.status}'`);
  }
}

export async function verify2fa(otp: string): Promise<OtpVerificationResult> {
  const response = await fetch(`${Path['2faVerification']}`, {
    method: 'POST',
    body: JSON.stringify({ data: { otp } }),
    credentials: 'include',
    headers: { 'Accept-Version': '2', 'Content-Type': 'application/json' },
  });

  switch (response.status) {
    case 204:
      return Result.Ok();
    case 422:
      return Result.Err('invalid-otp');
    case 412:
      return Result.Err('timeout');
    default:
      throw new Error(`Unexpected status '${response.status}'`);
  }
}

export type Delete2faError =
  'invalid-otp' | 'invalid-recovery-code' | 'timeout';
export type Delete2faResult = Result<void, Delete2faError>;

export async function delete2fa(
  credential: { otp: string } | { recoveryCode: string },
): Promise<Delete2faResult> {
  const response = await fetch(`${Path['2fa']}`, {
    method: 'DELETE',
    body: JSON.stringify({ data: credential }),
    credentials: 'include',
    headers: { 'Accept-Version': '2', 'Content-Type': 'application/json' },
  });

  switch (response.status) {
    case 204:
      return Result.Ok();
    case 422:
      if ('otp' in credential) {
        return Result.Err('invalid-otp');
      }
      return Result.Err('invalid-recovery-code');
    case 412:
      return Result.Err('timeout');
    default:
      throw new Error(`Unexpected status '${response.status}'`);
  }
}

export async function enable2fa(): Promise<Result<void, 'timeout'>> {
  const response = await fetch(`${Path['2fa']}`, {
    method: 'PATCH',
    body: JSON.stringify({ data: { enabled: true } }),
    credentials: 'include',
    headers: { 'Accept-Version': '2', 'Content-Type': 'application/json' },
  });

  switch (response.status) {
    case 200:
      return Result.Ok();
    case 412:
      return Result.Err('timeout');
    default:
      throw new Error(`Unexpected status '${response.status}'`);
  }
}

type LoginSSOSuccessResponse =
  paths[Path['loginSSO']]['post']['responses']['201']['content']['application/json'];
export type LoginSSOError = 'auth-failed' | 'invalid';
export type LoginSSOResult = Result<{ redirectUrl: string }, LoginSSOError>;

export async function loginSSO(
  email: string,
  returnUrl: string | undefined,
): Promise<LoginSSOResult> {
  const response = await fetch(`${cortexBrowserEndpoint}${Path.loginSSO}`, {
    method: 'POST',
    body: JSON.stringify({
      data: { email, returnUrl },
    }),
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });

  switch (response.status) {
    case 201: {
      const { data }: LoginSSOSuccessResponse = await response.json();
      return Result.Ok({ redirectUrl: data.redirectUrl });
    }
    case 401:
    case 403:
      return Result.Err('invalid');
    case 504:
      return Result.Err('auth-failed');
    default:
      throw new Error(`Unexpected status '${response.status}'`);
  }
}

export async function requestPasswordReset(email: string) {
  const response = await fetch(
    `${cortexBrowserEndpoint}${Path.requestPasswordReset}`,
    {
      method: 'POST',
      body: JSON.stringify({
        data: { email },
      }),
      headers: { 'Content-Type': 'application/json' },
    },
  );

  if (response.status === 204) return;
  throw new Error(`Unexpected status '${response.status}'`);
}

export async function resetPassword(
  password: string,
  passwordConfirmation: string,
) {
  const response = await fetch(
    `${cortexBrowserEndpoint}${Path.passwordReset}`,
    {
      method: 'POST',
      body: JSON.stringify({
        data: { password, passwordConfirmation },
      }),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  switch (response.status) {
    case 204:
      return Result.Ok();
    case 404:
      return Result.Err('no-token');
    case 422:
      return Result.Err('identical-password');
    default:
      throw new Error(`Unexpected status '${response.status}'`);
  }
}

export async function setInitialPassword(
  password: string,
  passwordConfirmation: string,
) {
  const response = await fetch(
    `${cortexBrowserEndpoint}${Path.initialPassword}`,
    {
      method: 'POST',
      body: JSON.stringify({
        data: { password, passwordConfirmation },
      }),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  switch (response.status) {
    case 204:
      return Result.Ok();
    case 404:
      return Result.Err('no-token');
    default:
      throw new Error(`Unexpected status '${response.status}'`);
  }
}
